import { useLocation, useHistory } from 'react-router-dom';
import { useCallback, useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { useLocalStorage } from '../../services/hooks/useLocalStorage';
import { useTranslation } from 'react-i18next';

import { getWorkflowUI, sendLogsToMfp } from '../../services/api';
import { useInterval } from '../../services/hooks/useInterval';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import { STC_ID, STRATUS_ID } from '../../services/constants';
import Header from '../Header';
import Loading from '../Loading';
import Success from '../Success';
import Failure from '../Failure';

const Processing = () => {
    const [noDipa] = useLocalStorage("noDipa");
    const [tenant, setTenant] = useLocalStorage('tenant', undefined);
    const [backgroundProcessing, setBackgroundProcessing] = useState(false);
    const [split, setSplit] = useState(false);
    const [delay] = useState(500);
    const [isRunning, setIsRunning] = useState(true);
    const [stratus, setStratus] = useLocalStorage("stratus", false);
    const ns = stratus ? 'stratus' : 'scantrip';
    const [inIFrame, setInIFrame] = useState(false);
    const [iframeMessage, setIFrameMessage] = useState('');
    const [iframeSuccess, setIFrameSuccess] = useState(false);
    const [iframeDone, setIFrameDone] = useState(false);
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation(ns);
    const { promiseInProgress } = usePromiseTracker();

    // Imports strictly for debug logs
    const [appVersion] = useLocalStorage('appVersion', '')
    const [user] = useLocalStorage('user', '');
    const [accessToken] = useLocalStorage('accessToken', undefined);
    const [refreshToken] = useLocalStorage('refreshToken', undefined);
    const [client] = useLocalStorage('client', undefined);
    const [lastStatusCheck, setLastStatusCheck] = useState({});
    const [apiError, setApiError] = useState('');

    const isInIFrame = () => {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    const sendAuthRequest = () => {
        if (!window.top) {
            return;
        }
        window.top.postMessage({
            source: 'dp-cloud-device-portal',
            payload: { type: 'auth-request' }
        }, '*');
    }

    const sendNextUrlToSite = (url) => {
        if (!window.top) {
            return;
        }
        window.top.postMessage({
            source: 'dp-cloud-device-portal',
            payload: { type: 'next-url', url }
        }, '*');
    }

    const sendJobDone = (success) => {
        if (!window.top) {
            return;
        }
        const url = `${window.location.origin}/${success ? 'success' : 'failure'}`;
        window.top.postMessage({
            source: 'dp-cloud-device-portal',
            payload: { type: 'job-done', url }
        }, '*');
    }

    const listenForMessage = (event) => {
        console.log(event);
        if (event?.data?.payload?.tenant) {
            const messageTenant = event.data.payload.tenant ?? {};
            messageTenant.iframe = true;
            setTenant(messageTenant);
            const isPlanStratus = event.data.payload?.brand === 'stratus';
            isPlanStratus ? setStratus('stratus') : setStratus('scantrip');
        }
    }

    useInterval(async () => {
        if (promiseInProgress) {
            return;
        }
        if (split) {
            return;
        }
        if (isInIFrame()) {
            setInIFrame(true);
            sendAuthRequest();
        }
        const arn = location.state?.arn || new URLSearchParams(location.search).get("arn");
        if (!arn) {
            setApiError('no arn');
            if (inIFrame) {
                setIFrameMessage(t('processing:no-arn'));
                sendJobDone(false);
                return;
            }
            history.push('/', { success: false });
        }
        try {
            if (!tenant?.region) {
                setApiError('no tenant region');
                console.error('Missing tenant region!');
                return;
            }
            const response = await trackPromise(getWorkflowUI(arn, tenant.region));
            if (response && Object.keys(response).length === 0) {
                setApiError('no response');
                if (inIFrame) {
                    setIFrameMessage(t('processing:no-response'));
                    sendJobDone(false);
                    return;
                }
                history.push('/', { success: false });
                return;
            }
            setLastStatusCheck(response);
            if (response.background || response.split) {
                setBackgroundProcessing(true);
                if (response.split) {
                    setIFrameMessage(t('processing:iframe:split'));
                    const url = `${window.location.origin}/split`;
                    sendNextUrlToSite(url);
                    setSplit(true);
                } else {
                    setIFrameMessage(t('processing:iframe:background'));
                    const url = `${window.location.origin}/background`;
                    sendNextUrlToSite(url);
                }
            }
            if (!response.processing) {
                if (response.waitingForInput) {
                    setIsRunning(false);
                    const metadataObject = JSON.parse(decodeURIComponent(response.metadata))
                    // removing key startingUser from metadata
                    const metadata = {
                        stratus: metadataObject.stratus,
                        user: metadataObject.user,
                        userId: metadataObject.userId,
                        token: metadataObject.token,
                        tenant: metadataObject.tenant,
                        jobName: metadataObject.jobName,
                        hideLogout: metadataObject.hideLogout ?? false
                    }

                    const params = new URLSearchParams({
                        arn: response.arn,
                        token: response.token,
                        url: response.callbackUrl,
                        workflowId: response.workflowId,
                        nodeId: response.nodeId,
                        metadata: JSON.stringify(metadata)
                    });

                    const redirectUrl = `${response.url}?${params.toString()}`;
                    sendNextUrlToSite(redirectUrl);
                    window.location = redirectUrl;
                } else if (response.done && !response.split) {
                    setIsRunning(false);
                    if (inIFrame) {
                        setIFrameSuccess(response.success);
                        setIFrameDone(true);
                        sendJobDone(true);
                        return;
                    }
                    history.push('/', { success: response.success, });
                }
            }
        } catch (e) {
            setApiError(`Other error ${e}`)
            console.error(e);
            setIsRunning(false);
            if (inIFrame) {
                setIFrameMessage(t('processing:error'));
                sendJobDone(false);
                return;
            }
            history.push('/', { success: false });
        }
    }, isRunning ? delay : null);

    const handleDebugLog = useCallback(async () => {
        const debugBody = {
            logSourceComponent: 'processing',
            domain: window.location.hostname,
            address: window.location.href,
            version: appVersion,
            promiseInProgress,
            noDipa,
            user,
            tenant,
            accessToken,
            refreshToken,
            now: Math.floor(Date.now() / 1000),
            client,
            stratus,
            inIFrame,
            iframeDone,
            iframeMessage,
            iframeSuccess,
            isRunning,
            lastStatusCheck,
            apiError,
        }
        await sendLogsToMfp(debugBody, stratus ? STRATUS_ID : STC_ID);
    }, [appVersion, promiseInProgress, noDipa, user, tenant, accessToken, refreshToken, client, stratus, apiError, iframeDone, iframeMessage, iframeSuccess, inIFrame, isRunning, lastStatusCheck]);

    useEffect(() => {
        window.addEventListener('message', listenForMessage);
        return () => {
            window.removeEventListener('message', listenForMessage);
        }
        // eslint-disable-next-line
    }, [])

    const handleKeyPress = useCallback((event) => {
        if (event.ctrlKey && event.code === "KeyL") {
            handleDebugLog();
        }
    }, [handleDebugLog]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleKeyPress]);

    const processingDisplay = (text, subtext, buttonText) => {
        if (backgroundProcessing) {
            return (
                <Loading
                    hideIcon={!inIFrame}
                    text={text}
                    subtext={subtext}
                    button={buttonText ? (
                        <Button variant="contained" onClick={() => history.push('/')}>{buttonText}</Button>
                    ) : (<div></div>)}
                    clickFunction={handleDebugLog}
                />
            );
        }
        return (
            <Loading
                clickFunction={handleDebugLog}
            />
        );
    }

    // iframe check isn't instantenous but tenant check should be. Hide header if tenant is undefined

    return (
        <>
            {inIFrame ? (
                iframeDone ? (
                    iframeSuccess ? (
                        <Success />
                    ) : (
                        <Failure />
                    )
                ) : (
                    <div>
                        {processingDisplay(iframeMessage)}
                    </div>
                )
            ) : (
                <div>
                    {tenant && !tenant.iframe && (<Header noDipa={noDipa} />)}
                    <div>
                        {processingDisplay(t('Your scanned document is being processed by ScanTrip Cloud.'), t('You may wait to input any additional information required by the workflow or return to the home screen to continue using workflows.'), t('Home'))}
                    </div>
                </div>
            )}
        </>
    )
}

export default Processing